import react, { useState } from "react";
import AddSocialLinks from "./AddSocialLinks";
import GuideLinkIcon from "./SocialLinkIcon";
import SelectCountryCode from "./SelectCountryCode";
import GuideWork from "./GuideWork";
import SelectRealRoleModal from "./SelectRealRoleModal";
import SetAvailabilityModal from "./SetAvailabilityModal";
import WebAlert from "./WebAlert";
import SelectGuideTypesModal from "./SelectGuideTypesModal";
import countries from "./countries.json";
import { alertNativeFirst, getGuideTypeImageUrlByHyphenatedTitle, getGuideTypeLabel, fetchProfileData  } from "./utils";
import defaultProfilePicture from "./default-boggler-guide-profile-picture.png";
import Footer from "./Footer";
import GuideDashboardHeader from "./GuideDashboardHeader";
import ManageAccount from "./ManageAccount";
import TimeZonePicker from "./TimeZonePicker";
import PublicGuideProfileCard from "./PublicGuideProfileCard";

const API_URL = process.env.REACT_APP_API_URL || "https://the-boggler-api-96bc2fa97f2d.herokuapp.com/api";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const convertMilitaryToAMPM = (time) => {
    const [hours, minutes] = time?.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const adjustedHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${adjustedHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const compareDays = (a, b) => {
    return daysOfWeek.indexOf(a.day) - daysOfWeek.indexOf(b.day);
};

const GuideProfile = (props) => {

    const sessionToken = window.localStorage.getItem('guideToken');

    const [profileData, setProfileData] = react.useState(null);
    const [editingProfile, setEditingProfile] = react.useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [telPrefix, setTelPrefix] = useState("");
    const [isWhatsapp, setIsWhatsapp] = useState(true);
    const [theProfilePictureUrl, setProfilePictureUrl] = useState(null);
    const [myRoles, setMyRoles] = useState([]);
    const [ageGroup, setAgeGroup] = useState(null);
    const [links, setLinks] = useState([]);
    const [motivation, setMotivation] = useState("");
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [usingDefaultProfilePicture, setUsingDefaultProfilePicture] = useState(true);
    const [doesAppointments, setDoesAppointments] = useState(null);
    const [address, setAddress] = useState(null);
    const [work, setWork] = useState(props.guideWork || []);
    const [showRealRoleModal, setShowRealRoleModal] = useState(false);
    const [encrypted_phone_number, setEncryptedPhoneNumber] = useState("");
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [availability, setAvailability] = useState([]);
    const [showWebAlert, setShowWebAlert] = useState({ show: false, title: "", message: "" });
    const [potentialGuideTypes, setPotentialGuideTypes] = useState(props.potentialGuideTypes || []);
    const [selectedGuideTypes, setSelectedGuideTypes] = useState([]);
    const [guideTypeLabels, setGuideTypeLabels] = useState([]);
    const [showAddCustomArchetypeModal, setShowAddCustomArchetypeModal] = useState(false);
    const [suggestedArchetypes, setSuggestedArchetypes] = useState([]);
    const [showSelectGuideTypesModal, setShowSelectGuideTypesModal] = useState(false);
    const [guideTypeImage, setGuideTypeImage] = useState(null);
    const [guideTypeImages, setGuideTypeImages] = useState([]);
    const [willUseWhatsapp, setWillUseWhatsapp] = useState(false);
    const [showConfirmWhatsappNumber, setShowConfirmWhatsappNumber] = useState(true);
    const [showConfirmPhoneNumber, setShowConfirmPhoneNumber] = useState(false);
    const [phoneConfirmationCode, setPhoneConfirmationCode] = useState("");
    const [whatsappConfirmationCode, setWhatsappConfirmationCode] = useState("");
    const [showConfirmNumberButton, setShowConfirmNumberButton] = useState(true);
    const [showConfirmationCodeSentMessage, setShowConfirmationCodeSentMessage] = useState(false);  
    const [dayClicked, setDayClicked] = useState(null);
    const [timeZone, setTimeZone] = useState(null);
    const [username, setUsername] = useState("");
    const [userNameAvailable, setUserNameAvailable] = useState(true);

    const [madeEdit, setMadeEdit] = useState(false);

    react.useEffect(() => {
        const fetchData = async () => {
            const data = await fetchProfileData();
            if (!data.success) {
                if (data.expiredToken) {
                    localStorage.removeItem('guideToken');
                    localStorage.removeItem('guideTokenExpiry');
                    window.location.href = '/';
                }
                return;
            } else {
                setProfileData(data.guide);
                setTimeZone(data.guide?.timeZone || null);
                let theSelectedGuideTypeLabels = [];
                let guideTypeImagesArray = [];
                if(data.guide?.confirmedWhatsappNumber) {
                    setShowConfirmWhatsappNumber(false);
                }
                if(data.guide?.confirmedPhoneNumber) {
                    setShowConfirmPhoneNumber(false);
                }
                if(data.guide?.types && data.guide.types.length > 0) {
                    for(const type of data.guide.types) {
                        let imageUrl = `https://buckish-ghosted-maps.s3.us-west-1.amazonaws.com/default-boggler-guide-profile-picture.png`;
                        try {
                            imageUrl = await getGuideTypeImageUrlByHyphenatedTitle(type);
                        } catch (error) {
                            console.error("Error fetching image URL for type:", type, error);
                        }
                        guideTypeImagesArray.push({ type: type, image: imageUrl });
                        let theGuideType = getGuideTypeLabel(type);
                        theSelectedGuideTypeLabels.push(theGuideType);
                    }
                }
                if(data.guide?.phoneTelPrefix) {
                    setTelPrefix(data.guide.phoneTelPrefix);
                }
                setUsername(data.guide.username || "");
                setGuideTypeImages(guideTypeImagesArray);
                setMyRoles(data.guide?.realRoles || []);
                setSelectedGuideTypes(data.guide?.types || []);
                setGuideTypeLabels(theSelectedGuideTypeLabels);
                setEncryptedPhoneNumber(data.weakly_encrypted_phone_number || "");
            }

            if (!data.usingDefaultProfilePicture) {
                setUsingDefaultProfilePicture(false);
            }
        };

        fetchData();
    }, []);

    const editProfileDetails = () => {
        setName(profileData?.name || "");
        setAvailability(profileData?.availability || []);
        setEmail(profileData?.email || "");
        setPhone(profileData?.phone_number || "");
        setIsWhatsapp(profileData?.is_whatsapp || false);
        setWillUseWhatsapp(profileData?.willUseWhatsapp)
        setEditingProfile(true)
        setProfilePictureUrl(profileData?.profilePictureUrl);
        setMyRoles(profileData?.realRoles || []);
        setAgeGroup(profileData?.age);
        setLinks(profileData?.social_links || []);
        setMotivation(profileData?.motivation || "");
        setSelectedCountry(profileData?.country || null);
        if(profileData?.phoneTelPrefix) {
            setTelPrefix(profileData?.phoneTelPrefix);
        }
        setDoesAppointments(profileData?.doesAppointments || null);
        setAddress(profileData?.meetingAddress ? {
            address: profileData.meetingAddress,
            coordinates: [
                profileData.meetingAddressCoordinates?.coordinates[0],
                profileData.meetingAddressCoordinates?.coordinates[1]
            ]
        } : null);
        setWork(profileData?.work || []);
        setMadeEdit(false);
    };

   const removeProfilePicture = () => {

        setMadeEdit(true);

            // Here you would typically upload the file to your server or a cloud storage service
        fetch(API_URL + "/conversations/remove-profile-picture/" + phone, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${sessionToken}`
            },
            body: JSON.stringify({
                forGuide: true
            })
        })
        .then((response) => response.json())
        .then((data) => {
            const { success } = data;
            if(success) {
                setProfilePictureUrl(null);
            }
        })
        .catch((error) => {
            console.error("Error uploading profile picture:", error);
        });
    }

   const uploadProfilePicture = async (file, options = {}) => {
        try {
            // 1. Get pre-signed upload URL
            const urlResponse = await fetch(`${API_URL}/conversations/generate-profile-picture-url/${phone}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileType: file.type,
                    forGuide: true
                })
            });

            const urlData = await urlResponse.json();

            if (!urlData.success) {
                console.log(urlData.message);
            }

            // 2. Upload file directly to S3
            const uploadResponse = await fetch(urlData.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (!uploadResponse.ok) {
                console.log('Failed to upload to S3');
            }

            // 3. Confirm upload with your backend
            const confirmResponse = await fetch(`${API_URL}/conversations/confirm-profile-picture-upload/${phone}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    imageKey: urlData.imageKey,
                    forGuide: true
                })
            });

            const confirmData = await confirmResponse.json();

            if (confirmData.success) {
                setProfilePictureUrl(confirmData.profilePictureURL);
                return confirmData;
            } else {
                console.log(confirmData.message);
            }

        } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
        }
    };

    // Usage:
    const handleFileUpload = async (selectedFile) => {
        setMadeEdit(true);
        try {

            const result = await uploadProfilePicture(selectedFile, {
                forGuide: true,
            });

            setProfilePictureUrl(result.profilePictureURL);

        } catch (error) {
            alertNativeFirst({ title: "Upload failed", message: error.message, setShowWebAlert: setShowWebAlert});
        }
    };

    const editGuide = () => {
        if(!name || name.trim() === "") {
            alertNativeFirst({ title: "Input Error", message: "Please enter your name.", setShowWebAlert: setShowWebAlert });
            return;
        }
        if(!ageGroup) {
            alertNativeFirst({ title: "Input Error", message: "Please select your age group.", setShowWebAlert: setShowWebAlert });
            return;
        }
        if(!selectedCountry) {
            alertNativeFirst({ title: "Input Error", message: "Please select your country of residence.", setShowWebAlert: setShowWebAlert });
            return;
        }

        if(!userNameAvailable) {
            alertNativeFirst({ title: "Input Error", message: "Username is already taken. Please choose another one.", setShowWebAlert: setShowWebAlert });
            return;
        }

        if(!timeZone) {
            alertNativeFirst({ title: "Input Error", message: "Please select your time zone.", setShowWebAlert: setShowWebAlert });
            return;
        }

        fetch(API_URL + "/guide/edit-guide-profile", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${window.localStorage.getItem("guideToken")}`
            },
            body: JSON.stringify({
                name: name,
                username: username,
                email: email,
                phone: phone,
                telPrefix: telPrefix,
                is_whatsapp: isWhatsapp,
                age: ageGroup,
                willUseWhatsapp: willUseWhatsapp,
                country: selectedCountry,
                types: selectedGuideTypes,
                roles: myRoles,
                links: links,
                doesAppointments: doesAppointments,
                meetingAddress: address?.address || null,
                meetingAddressCoordinates: address?.coordinates || null,
                work: work,
                motivation: motivation,
                timeZone: timeZone
            })
        })
        .then((response) => response.json())
        .then((data) => {
            if(data.success) {
                // Optionally clear the input fields
                setEditingProfile(false);
                // Refresh profile data
                fetchProfileData();
                setMadeEdit(false);
            } else {
                if(data.expiredToken) {
                    props.showTheExpiredTokenModal();
                    return;
                }
                alertNativeFirst({ title: "Error", message: "Failed to send message. Please try again.", setShowWebAlert: setShowWebAlert });
            }
        })
        .catch((error) => {
            console.error("Error sending message:", error);
            alertNativeFirst({ title: "Error", message: "Failed to send message. Please try again.", setShowWebAlert: setShowWebAlert });
        });
    }

    const checkUsernameAvailability = async (username) => {
        try {
            const response = await fetch(`${API_URL}/guide/check-username-availability`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ username })
            });
            const data = await response.json();
            return data.available; // true if available, false if taken
        } catch (error) {
            console.error("Error checking username availability:", error);
            return false;
        }
    };

    const addGuideLinks = (links) => {
        setLinks(links);
        setMadeEdit(true);
    }

    const selectAgeGroup = (event) => {
        const value = event.target.value;
        setAgeGroup(value);
        setMadeEdit(true);
    }

    const handleCountryChange = (country) => {

        if(country && country.code) {
            setSelectedCountry(country);
        }

        setMadeEdit(true);
    }

    const unSnakeCase = (text) => {
        if(!text) return "";
        return text.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }

    const addRealRole = (role) => {
        setMyRoles((prev) => [...prev, role]);
        setMadeEdit(true);
    }

    const removeRealRole = (role) => {
        setMyRoles((prev) => prev.filter((r) => r.title !== role.title));
        setMadeEdit(true);
    }

    const getArticleForRole = (roleTitle) => {
        const vowels = ['a', 'e', 'i', 'o', 'u'];
        if(!roleTitle || roleTitle.length === 0) return "a";
        const firstLetter = roleTitle?.charAt(0)?.toLowerCase();
        return vowels.includes(firstLetter) ? "an" : "a";
    }

    const getMyRolesAsString = () => {
        if(!myRoles || myRoles.length === 0) return "a Friend";
        if(myRoles.length === 1) return " " + getArticleForRole(myRoles[0].title) + " " + myRoles[0].title+ ", and Friend";
        if(myRoles.length === 2) return " " + getArticleForRole(myRoles[0].title) + " " + myRoles[0].title + ", " + getArticleForRole(myRoles[1].title) + " " + myRoles[1].title+ ", and Friend";
        let rolesString = "";
        if(myRoles.length > 2) {
            for(let i = 0; i < myRoles.length; i++) {
                if(i < myRoles.length -1) {
                    rolesString += myRoles[i].title + ", ";
                } else {
                    rolesString += myRoles[i].title;
                }
            }
            return getArticleForRole(myRoles[0].title)+" " + rolesString+ ", and Friend";
        }
    }

    const handleShowAvailabilityModal = () => {
        setShowAvailabilityModal(true);
    }

     const addGuideType = async (hyphenatedTitle) => {
        setMadeEdit(true);
        if(hyphenatedTitle === "other") {
            setShowAddCustomArchetypeModal(true);
            return;
        }
        if(hyphenatedTitle && hyphenatedTitle !== "" && !selectedGuideTypes.includes(hyphenatedTitle)) {
            setSelectedGuideTypes([...selectedGuideTypes, hyphenatedTitle]);
            if(suggestedArchetypes.find((archetype) => archetype?.hyphenated_title === hyphenatedTitle) === undefined) {
                const imageUrl = await getGuideTypeImageUrlByHyphenatedTitle(hyphenatedTitle);
                setGuideTypeImage(imageUrl);
            } else {
                const archetypeInfo = suggestedArchetypes.find((archetype) => archetype?.hyphenated_title === hyphenatedTitle);
                let newImageUrl = archetypeInfo?.profilePictureKey || `https://buckish-ghosted-maps.s3.us-west-1.amazonaws.com/default-boggler-guide-profile-picture.png`;
                setGuideTypeImage(newImageUrl);
            }
            return
        }
        return   
    }

    const removeGuideType = async (hyphenatedTitle) => {
        setMadeEdit(true);
        const updatedGuideTypes = selectedGuideTypes.filter((type) => type !== hyphenatedTitle);
        setSelectedGuideTypes(updatedGuideTypes);
        const imageUrl = await getGuideTypeImageUrlByHyphenatedTitle(updatedGuideTypes[updatedGuideTypes.length - 1] || "");
        setGuideTypeImage(imageUrl);
    }

    const addSuggestedArchetype = (suggestedArchetype) => {
        setShowAddCustomArchetypeModal(false);
        setSuggestedArchetypes((prev) => [...prev, suggestedArchetype]);
        let thePotentialGuideTypes = [];
        thePotentialGuideTypes.push({
            hyphenated_title: suggestedArchetype.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, ''),
            title: "✏️ "+suggestedArchetype.title+" (Pending Review)",
            description: suggestedArchetype.description,
            profilePictureKey: suggestedArchetype.profileImageKey || "other",
            presumedGender: suggestedArchetype.presumedGender,
            pronoun1: suggestedArchetype.pronoun1,
            pronoun2: suggestedArchetype.pronoun2
        });
        setMadeEdit(true);
        thePotentialGuideTypes.push(...potentialGuideTypes);
        setPotentialGuideTypes(thePotentialGuideTypes);
        const hyphenatedTitle = suggestedArchetype.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '');
        setSelectedGuideTypes([...selectedGuideTypes, hyphenatedTitle]);
        setGuideTypeImage(`https://buckish-ghosted-maps.s3.us-west-1.amazonaws.com/default-boggler-guide-profile-picture.png`);
    }

    const sendWhatsappNumberConfirmationCode = async () => {
        let response = null;
        try {
            response = await fetch(API_URL + "/guide/send-whatsapp-number-confirmation-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    whatsapp_number: phone
                })
            });
            const data = await response.json();
            if(data && data.success) {
                setShowConfirmationCodeSentMessage(true);
                setShowConfirmNumberButton(false)
                setTimeout(() => {
                    setShowConfirmationCodeSentMessage(false);
                    setShowConfirmNumberButton(true);
                }, 20000);
            }
        } catch (error) {
            console.error("Error sending WhatsApp number confirmation code:", error);
        }
    }

    const sendPhoneNumberConfirmationCode = async () => {
        let response = null;
        try {
            response = await fetch(API_URL + "/guide/send-phone-number-confirmation-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    phone_number: phone
                })
            });
            const data = await response.json();
            if(data && data.success) {
                setShowConfirmationCodeSentMessage(true);
                setShowConfirmNumberButton(false)
                setTimeout(() => {
                    setShowConfirmationCodeSentMessage(false);
                    setShowConfirmNumberButton(true);
                }, 20000);
            }
        } catch (error) {
            console.error("Error sending phone number confirmation code:", error);
        }
    }

    const verifyPhoneNumberConfirmationCode = async () => {
        let response = null;
        try {
            response = await fetch(API_URL + "/guide/verify-phone-number-confirmation-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    phone_number: phone,
                    confirmation_code: phoneConfirmationCode
                })
            })
            const data = await response.json();
            if(data && data.success) {
                setMadeEdit(true);
                setShowConfirmPhoneNumber(false);
            }
        } catch (error) {
            console.error("Error verifying phone number confirmation code:", error);
        }
    }

    const verifyWhatsappNumberConfirmationCode = async () => {
        let response = null;
        try {
            response = await fetch(API_URL + "/guide/verify-whatsapp-number-confirmation-code", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    phone_number: phone,
                    confirmation_code: whatsappConfirmationCode
                })
            });
            const data = await response.json();
            if(data && data.success) {
                setShowConfirmWhatsappNumber(false);
                setMadeEdit(true);
            }
        } catch (error) {
            console.error("Error verifying WhatsApp number confirmation code:", error);
        }
    }

    const handleWhatsAppOrPhoneChange = (event) => {
        setMadeEdit(true); 
        setIsWhatsapp(event.target.value === "true")
        if(event.target.value === "true") {
            setShowConfirmWhatsappNumber(true);
            setShowConfirmNumberButton(true);
            setShowConfirmPhoneNumber(false);
        } else {
            setShowConfirmNumberButton(true);
            setShowConfirmPhoneNumber(true);
            setShowConfirmWhatsappNumber(false);
        }
    }

    const handleDayClick = (day) => {
        setDayClicked(day);
        setShowAvailabilityModal(true);
    }

    const handleUserNameInputChange = async (event) => {
        setMadeEdit(true);
        setUsername(event.target.value);
        const isAvailable = await checkUsernameAvailability(event.target.value);
        setUserNameAvailable(isAvailable);
    }

    return (
        <>
            <GuideDashboardHeader />
            {showWebAlert.show && <WebAlert {...showWebAlert} setShowWebAlert={setShowWebAlert} />}
            {showRealRoleModal && <SelectRealRoleModal
                gender={profileData?.gender}
                show={showRealRoleModal}
                setShow={setShowRealRoleModal}
                selectedRealRoles={myRoles}
                addRealRole={addRealRole}
                removeRealRole={removeRealRole}
            />}
            {showSelectGuideTypesModal &&  <SelectGuideTypesModal
                show={showSelectGuideTypesModal}
                setShow={setShowSelectGuideTypesModal}
                selectedGuideTypes={selectedGuideTypes || []}
                addGuideType={addGuideType}
                removeGuideType={removeGuideType}
                potentialGuideTypes={potentialGuideTypes}
                suggestedArchetypes={suggestedArchetypes}
                currentGuideTypes={selectedGuideTypes}
                addSuggestedArchetype={addSuggestedArchetype}
                setShowAddCustomArchetypeModal={setShowAddCustomArchetypeModal}
            />}
            {showAvailabilityModal && <SetAvailabilityModal
                guideId={profileData?._id}
                encrypted_phone_number={encrypted_phone_number}
                setMadeEdit={setMadeEdit}
                sessionToken={sessionToken}
                dayClicked={dayClicked}
                availability={availability}
                setAvailability={setAvailability}
                show={showAvailabilityModal}
                setShow={setShowAvailabilityModal}
                showTheExpiredTokenModal={props.showTheExpiredTokenModal}
            />}
            <section className="main-body-section">
                {!editingProfile && (
                    <>
                        <div className="guide-profile-container">
                            <PublicGuideProfileCard guideData={profileData} />
                        </div>
                        <button style={{margin: "0 auto", display: "block"}} className="edit-profile-button" onClick={() => editProfileDetails(profileData)}>Edit Details</button>   
                    </>
                )}
                {editingProfile && (
                    <>      
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '5px'}}>
                                <span onClick={() => setShowRealRoleModal(true)} className="show-real-role-modal-button" id="trigger-show-real-role-modal-button">In the real world, I am... {getMyRolesAsString()}</span>
                            </div>
                            <div className="edit-profile-outer-container">
                                <div className="editprofilesection">
                                    {(!theProfilePictureUrl || usingDefaultProfilePicture) ? (
                                        <div style={{marginTop: '20px'}}>
                                                <label htmlFor="profile-picture" style={{fontSize: "1rem"}}>Profile Picture (optional):</label>
                                                <br />
                                                <input id="profile-picture" onChange={(e) => handleFileUpload(e.target.files[0])} type="file" accept="image/*" style={{padding: '10px', fontSize: '1rem', width: '80%', maxWidth: '300px', marginTop: '10px'}} />
                                        </div>
                                    ) : (
                                        <div style={{margin: '20px auto', position: 'relative'}}>
                                            <div className="guide-profile-picture-editing" style={{backgroundImage: `url(${theProfilePictureUrl})`}} />
                                            <button 
                                                className="remove-picture-button"
                                                onClick={removeProfilePicture}
                                            >
                                                🗑️
                                            </button>                                            
                                        </div>
                                    )}
                                    <div style={{margin: '0 auto', flexDirection: 'column', display: 'flex', alignItems: 'center'}}>
                                        <input value={name} onChange={(e) => { setMadeEdit(true); setName(e.target.value); }} id="your-name" type="text" placeholder="Enter your name" style={{padding: '10px', fontSize: '1rem', width: '100%', maxWidth: '300px', marginTop: '10px', textAlign: 'center'}} /><br />
                                    </div>
                                    <div style={{margin: '0 auto', flexDirection: 'column', display: 'flex', alignItems: 'center'}}>
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            @<input
                                                value={username}
                                                onChange={handleUserNameInputChange}
                                                id="your-username"
                                                type="text"
                                                placeholder="Enter your username"
                                                style={{padding: '10px', fontSize: '1rem', width: '100%', maxWidth: '300px', marginTop: '10px', textAlign: 'center'}}
                                            />
                                            {username && userNameAvailable === true && (
                                                <span title="Username available" style={{color: 'green', fontSize: '1.5rem'}}>✔️</span>
                                            )}
                                            {username && userNameAvailable === false && (
                                                <span title="Username not available" style={{color: 'red', fontSize: '1.5rem'}}>❌</span>
                                            )}
                                            {username && userNameAvailable === null && (
                                                <span title="Checking..." style={{color: 'gray', fontSize: '1.2rem'}}>⏳</span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{margin: '20px auto', flexDirection: 'column', display: 'flex', alignItems: 'center'}}>
                                        <p>{selectedGuideTypes?.map((type) => (
                                            <span style={{margin: '0 5px'}} key={type}>{getGuideTypeLabel(type)}</span>
                                        ))}</p>
                                        <button id="open-select-guide-types-modal" className="show-archetypes-modal-button" onClick={() => setShowSelectGuideTypesModal(true)}>
                                            Manage Archetypes
                                        </button>
                                    </div>
                                    <span>My Age Group is...</span>
                                    <div style={{display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '10px', maxWidth: '300px'}}>
                                        <select name="age-group" value={ageGroup} onChange={selectAgeGroup} className={"select-element survey-option"+(ageGroup ? " selected" : "")}>
                                            <option className="select-option" value="under_18" selected={ageGroup === "under_18"}>Under 18</option>
                                            <option className="select-option" value="18-25" selected={ageGroup === "18-25"}>18-25</option>
                                            <option className="select-option" value="26-35" selected={ageGroup === "26-35"}>26-35</option>
                                            <option className="select-option" value="36-50" selected={ageGroup === "36-50"}>36-50</option>
                                            <option className="select-option" value="50-65" selected={ageGroup === "50-65"}>50-65</option>
                                            <option className="select-option" value="over_65" selected={ageGroup === "over_65"}>65+</option>
                                        </select>
                                    </div>
                                    <div style={{marginBottom: '10px', marginTop: '20px'}}>
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                            <span>Select your TimeZone:</span><br />
                                            <TimeZonePicker style={{marginTop: '10px'}} didMakeSelection={selectedCountry} setCountry={handleCountryChange} setTimeZone={(tz) => { setTimeZone(tz); setMadeEdit(true); }} selectedCountry={selectedCountry} timeZone={timeZone} />
                                        </div>
                                    </div>
                                </div>
                                <div className="editprofilesection">
                                    <h3>Share Your Social Links (optional)</h3>
                                    <AddSocialLinks onChange={addGuideLinks} links={links} />
                                    {links.length > 0 && (
                                        <>
                                            <h4>Your Links:</h4>
                                            <div className="profile-links-section">
                                                {links.map(({ platform, url }, i) => (
                                                    <div id={"social-icon"+platform+i} style={{position: "relative", display: 'flex', flexDirection: "column", alignItems: 'center', margin: '0 auto', padding: "5px"}} key={"link-item-"+platform+i}>
                                                        <GuideLinkIcon size={75} key={"social-icon"+platform+i} platform={platform} onClick={() => window.open(url, "_blank")} />
                                                        <span
                                                            aria-label={"social-icon"+platform+i}
                                                            onClick={() => {
                                                                setMadeEdit(true);
                                                                setLinks(links.filter(link => link.url !== url));
                                                            }} 
                                                            className="remove-social-link-button">
                                                            ❌
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <div style={{display: "flex", flexDirection: "column", marginTop: '20px'}}>
                                        <h3>Current Availability:</h3>
                                        <div className="available-days-flex-grid-profile">
                                            {daysOfWeek.map((d) => {
                                                const slotsForDay = availability.filter(slot => slot.day.toLowerCase() === d.toLowerCase());
                                                return (
                                                    <div onClick={() => handleDayClick(d)} className="available-days-flex-item" key={d}>
                                                        <h4 key={d} style={{fontWeight: "bold", marginTop: "10px"}}>{d}</h4>
                                                        {slotsForDay.sort((a, b) => compareDays(a, b) || a.from.localeCompare(b.from)).map((slotForDay, index) => {
                                                                    return (
                                                                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: "5px"}} key={slotForDay+" "+index}>
                                                                            {convertMilitaryToAMPM(slotForDay.from)} - {convertMilitaryToAMPM(slotForDay.to)}
                                                                        </div>
                                                                    );
                                                                })
                                                        }
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <div className="editprofilesection">
                                    <div style={{display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: '20px'}}>
                                        {work && (
                                            <GuideWork
                                                work={work}
                                                setWork={(work) => { setWork(work); setMadeEdit(true); }}
                                            />
                                        )}
                                    </div>
                                    <textarea id="motivation" value={motivation} placeholder="Why do you think you make a good guide on The Boggler? (optional)" style={{padding: '10px', fontSize: '1rem', width: '100%', maxWidth: '300px', height: '100%', minHeight: '175px', margin: '10px auto'}} onChange={(e) => {setMadeEdit(true); setMotivation(e.target.value)}}></textarea>
                                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "baseline"}}>
                                        <span htmlFor="whatsapp-phone-select">Phone Number - </span>
                                        <select className="form-select" id="whatsapp-phone-select" value={(!isWhatsapp && isWhatsapp !== "true") ? false : true} onChange={handleWhatsAppOrPhoneChange}>
                                            {/* <option value="false">SMS</option> */}
                                            <option value="true">WhatsApp</option>
                                        </select>
                                    </div>
                                    <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "baseline", marginTop: '10px'}}>
                                        <label htmlFor="phone">Phone Number:</label>
                                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: '10px'}}>
                                            <input
                                                name="phone"
                                                onChange={(e) => {
                                                    setMadeEdit(true);
                                                    setPhone(e.target.value);
                                                }}
                                                className="guide-signup-phone-input" 
                                                value={phone}
                                                type="tel" 
                                                placeholder={"Your "+(isWhatsapp ? "WhatsApp" : "Phone")+" Number"} 
                                            />
                                        </div>
                                    </div>
                                    <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "baseline", marginTop: '10px'}}>
                                        <label htmlFor="email">Email Address:</label>
                                        <input value={email} className="guide-signup-email-input" onChange={(e) => { setMadeEdit(true); setEmail(e.target.value)}} type="email" name="email" placeholder={"Your Email Address"} />
                                    </div>
                                    {showConfirmPhoneNumber && (
                                        <div style={{marginTop: '10px', color: 'green', fontWeight: 'bold'}}>
                                            {showConfirmNumberButton && <><button className="send-confirmation-code-button" onClick={sendPhoneNumberConfirmationCode}>Send Confirmation Code</button><br /></>}
                                            {showConfirmationCodeSentMessage && <><span style={{textAlign: "center", color: "green"}}>Confirmation Code Sent</span><br /></>}
                                            <input type="text" placeholder="Enter Confirmation Code" value={phoneConfirmationCode} onChange={(e) => setPhoneConfirmationCode(e.target.value)} className="confirmation-code-input" /><br />
                                            <button className="verify-confirmation-code-button" onClick={verifyPhoneNumberConfirmationCode}>Verify Code</button>
                                        </div>
                                    )}
                                    {showConfirmWhatsappNumber && (
                                        <div style={{marginTop: '10px', color: 'green', fontWeight: 'bold'}}>
                                            {showConfirmNumberButton && <><button className="send-confirmation-code-button" onClick={sendWhatsappNumberConfirmationCode}>Send Confirmation Code</button><br /></>}
                                            {showConfirmationCodeSentMessage && <><span style={{textAlign: "center", color: "green"}}>Confirmation Code Sent</span><br /></>}
                                            <input type="text" placeholder="Enter Confirmation Code" value={whatsappConfirmationCode} onChange={(e) => setWhatsappConfirmationCode(e.target.value)} className="confirmation-code-input" /><br />
                                            <button className="verify-confirmation-code-button" onClick={verifyWhatsappNumberConfirmationCode}>Verify Code</button>
                                        </div>
                                    )}
                                    <ManageAccount />
                                </div>
                            </div>
                            <div style={{width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", marginTop: '20px'}}>
                                <button id="save-profile-button" className={"profile-action-button "+ (madeEdit ? " madeEdit" : "")} onClick={madeEdit ? editGuide : () => setEditingProfile(false)}>{!madeEdit ? "Cancel" : "Save Profile"}</button>
                                {madeEdit && <button id="cancel-profile-button" onClick={() => setEditingProfile(false)} className={"profile-action-button madeEdit"} style={{marginTop: '20px'}}>Cancel</button>}
                            </div>
                        </>
                    )}
            </section>
            <Footer />
        </>
    );
}

export default GuideProfile;